import React from 'react';

/**
 * CircularLoader - A reusable circular loading spinner component
 * 
 * @param {Object} props
 * @param {boolean} props.isLoading - Controls visibility of the loader
 * @param {string} props.size - Size variant: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} props.color - Tailwind color class for the spinner (default: 'border-stone-800')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.overlay - If true, renders with a semi-transparent overlay background
 * @param {boolean} props.centered - If true, centers the loader in its container
 */
const CircularLoader = ({
    isLoading = false,
    size = 'md',
    color = 'border-stone-800',
    className = '',
    overlay = false,
    centered = false,
}) => {
    if (!isLoading) return null;

    // Size mappings
    const sizeClasses = {
        sm: 'h-4 w-4 border-2',
        md: 'h-6 w-6 border-2',
        lg: 'h-8 w-8 border-[3px]',
    };

    const spinnerSize = sizeClasses[size] || sizeClasses.md;

    const spinner = (
        <div
            className={`animate-spin rounded-full ${spinnerSize} border-transparent ${color} border-t-current ${className}`}
            role="status"
            aria-label="Loading"
        >
            <span className="sr-only">Loading...</span>
        </div>
    );

    // Overlay variant - covers entire viewport
    if (overlay) {
        return (
            <div className="fixed inset-0 bg-white/70 backdrop-blur-[1px] z-[9999] flex items-center justify-center">
                {spinner}
            </div>
        );
    }

    // Centered variant - centers within flex container
    if (centered) {
        return (
            <div className="flex items-center justify-center py-8">
                {spinner}
            </div>
        );
    }

    return spinner;
};

export default CircularLoader;
